import { useEffect, useRef, useState } from 'react';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { pxToRem } from '@pocketlaw/tetris';
import { useMount, useUnmount } from 'react-use';
import useMobile from '../hooks/useMobile';
const SIDEBAR_STATES = new Map();
const SIDEBAR_WIDTHS = new Map();
const SIDEBAR_REFS = new Map();
const LAYOUT_REFS = new Map();
export const LAYOUT_SIDEBAR_CSS_VARIABLE_WIDTH = '--screen-layout-sidebar-width';
export const LAYOUT_SIDEBAR_CSS_VARIABLE_WIDTH_TRANSITION = '--screen-layout-sidebar-width-transition';
export const SIDEBAR_MAX_WIDTH = 880;
export const SIDEBAR_MIN_WIDTH = 440;
export const SIDEBAR_WIDTH_TRANSITION_DURATION_MS = 200;
export const useScreenSidebar = (id) => {
    const isMobile = useMobile();
    // Get or create the reactive var for this sidebar
    if (!SIDEBAR_STATES.has(id)) {
        SIDEBAR_STATES.set(id, makeVar(!isMobile));
    }
    if (!SIDEBAR_WIDTHS.has(id)) {
        SIDEBAR_WIDTHS.set(id, makeVar(SIDEBAR_MIN_WIDTH));
    }
    if (!SIDEBAR_REFS.has(id)) {
        SIDEBAR_REFS.set(id, makeVar({ current: null }));
    }
    if (!LAYOUT_REFS.has(id)) {
        LAYOUT_REFS.set(id, makeVar({ current: null }));
    }
    const sidebarStateVar = SIDEBAR_STATES.get(id);
    const state = useReactiveVar(sidebarStateVar);
    const sidebarWidthVar = SIDEBAR_WIDTHS.get(id);
    const sidebarWidth = useReactiveVar(sidebarWidthVar);
    const sidebarRefVar = SIDEBAR_REFS.get(id);
    const sidebarRef = useReactiveVar(sidebarRefVar);
    const layoutRefVar = LAYOUT_REFS.get(id);
    const layoutRef = useReactiveVar(layoutRefVar);
    const [expanded, setExpanded] = useState(false);
    const timerRef = useRef();
    const set = (open) => sidebarStateVar(open);
    const open = () => set(true);
    const close = () => set(false);
    const toggle = () => set(!state);
    const setSidebarWidthVariable = (width) => {
        var _a;
        sidebarWidthVar(width);
        (_a = layoutRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty(LAYOUT_SIDEBAR_CSS_VARIABLE_WIDTH, pxToRem(width));
    };
    const animateSidebarWidth = (width) => {
        var _a;
        (_a = layoutRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty(LAYOUT_SIDEBAR_CSS_VARIABLE_WIDTH_TRANSITION, `width ${SIDEBAR_WIDTH_TRANSITION_DURATION_MS}ms ease-in-out`);
        setSidebarWidthVariable(width);
        timerRef.current = setTimeout(() => {
            var _a;
            (_a = layoutRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty(LAYOUT_SIDEBAR_CSS_VARIABLE_WIDTH_TRANSITION, `none`);
        }, SIDEBAR_WIDTH_TRANSITION_DURATION_MS);
    };
    const expand = () => {
        animateSidebarWidth(SIDEBAR_MAX_WIDTH);
    };
    const collapse = () => {
        animateSidebarWidth(SIDEBAR_MIN_WIDTH);
    };
    useMount(() => {
        setSidebarWidthVariable(sidebarWidth);
    });
    useUnmount(() => {
        clearTimeout(timerRef.current);
    });
    useEffect(() => {
        const sidebar = sidebarRef.current;
        if (!sidebar) {
            return undefined;
        }
        const observer = new ResizeObserver((entries) => {
            const [entry] = entries;
            const halfway = (SIDEBAR_MAX_WIDTH + SIDEBAR_MIN_WIDTH) / 2;
            setExpanded(entry.contentRect.width >= halfway);
        });
        observer.observe(sidebar);
        return () => {
            observer.disconnect();
        };
    }, [sidebarRef]);
    return {
        layoutRef,
        sidebarRef,
        setSidebarWidthVariable,
        open,
        close,
        toggle,
        expand,
        collapse,
        isOpen: state,
        isExpanded: expanded,
    };
};
